/**
 * React Query hooks over the generated API types.
 *
 * **The types come from the backend's own OpenAPI document**, not from hand-written interfaces
 * — `src/api/schema.ts` is generated by `npm run generate:api` and committed. That is §4's
 * "close the frontend/backend type contract automatically": a route that changes its response
 * shape breaks this file at compile time rather than at runtime in front of a user.
 *
 * Every hook takes its token from `useAuth` rather than accepting one, so no caller can
 * accidentally issue an unauthenticated request by forgetting to pass it. The query keys
 * include the uid for the same reason cache keys usually include an identity: signing out and
 * back in as somebody else must not serve the first user's history from cache.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";

import { useAuth } from "../auth/AuthProvider";
import { request } from "./client";
import type { components } from "./schema";

type Schemas = components["schemas"];

export type GenerationWithPreview = Schemas["GenerationWithPreview"];
export type HistoryResponse = Schemas["HistoryResponse"];
export type RetentionResponse = Schemas["RetentionResponse"];
export type RetentionUpdateResponse = Schemas["RetentionUpdateResponse"];
export type RateLimitStatus = Schemas["RateLimitStatus"];
export type RetentionPeriod = Schemas["RetentionPeriod"];
export type VisualPolicyProfileDoc = Schemas["VisualPolicyProfileDoc"];
export type AccessResponse = Schemas["AccessResponse"];

/**
 * One namespace for every key, so an invalidation cannot miss a query by spelling its key
 * differently from the hook that registered it.
 */
export const queryKeys = {
  history: (uid: string) => ["history", uid] as const,
  retention: (uid: string) => ["retention", uid] as const,
  rateLimit: (uid: string) => ["rate-limit", uid] as const,
  governanceProfiles: () => ["governance-profiles"] as const,
  series: (uid: string) => ["series", uid] as const,
  access: (uid: string) => ["access", uid] as const,
} as const;

/**
 * Whether a query should run at all.
 *
 * Not merely an optimisation: firing before the token settles produces a guaranteed 401, and
 * React Query would cache that failure and show the user an error for a request that was never
 * going to work. `useAuth().loading` is exactly "we do not know yet".
 */
function useReady(): { enabled: boolean; idToken: string | null; uid: string } {
  const { idToken, loading, user } = useAuth();
  return { enabled: !loading && Boolean(idToken), idToken, uid: user?.uid ?? "anonymous" };
}

/**
 * What the signed-in caller may do — today, only whether to offer the admin surface.
 *
 * **Asked of the backend, not read off the token.** The UI used to check the `role: "admin"`
 * claim itself; under Nexus (W1) admin is a permission in Nexus's decision, which only the
 * backend can ask for. `GET /access/me` answers from the same function `require_admin` uses, so
 * the links shown and the routes allowed cannot disagree. Convenience, not enforcement.
 */
export function fetchMyAccess(idToken: string | null, signal?: AbortSignal): Promise<AccessResponse> {
  return request<AccessResponse>("/access/me", { idToken, signal });
}

/** `fetchMyAccess` as a query, for the header. The `/admin` gate calls it directly — see there. */
export function useMyAccess(): UseQueryResult<AccessResponse> {
  const { enabled, idToken, uid } = useReady();
  return useQuery({
    queryKey: queryKeys.access(uid),
    enabled,
    queryFn: ({ signal }) => fetchMyAccess(idToken, signal),
  });
}

export function useHistory(): UseQueryResult<HistoryResponse> {
  const { enabled, idToken, uid } = useReady();
  return useQuery({
    queryKey: queryKeys.history(uid),
    enabled,
    queryFn: ({ signal }) => request<HistoryResponse>("/history", { idToken, signal }),
  });
}

export function useRetentionPeriod(): UseQueryResult<RetentionResponse> {
  const { enabled, idToken, uid } = useReady();
  return useQuery({
    queryKey: queryKeys.retention(uid),
    enabled,
    queryFn: ({ signal }) => request<RetentionResponse>("/settings/retention", { idToken, signal }),
  });
}

export function useRateLimit(): UseQueryResult<RateLimitStatus> {
  const { enabled, idToken, uid } = useReady();
  return useQuery({
    queryKey: queryKeys.rateLimit(uid),
    enabled,
    queryFn: ({ signal }) =>
      request<RateLimitStatus>("/generate/rate-limit", { idToken, signal }),
  });
}

export function useSetRetentionPeriod() {
  const { idToken, user } = useAuth();
  const queryClient = useQueryClient();
  const uid = user?.uid ?? "anonymous";

  return useMutation({
    mutationFn: (retentionPeriod: RetentionPeriod) =>
      request<RetentionUpdateResponse>("/settings/retention", {
        method: "PUT",
        body: { retentionPeriod },
        idToken,
      }),
    onSuccess: () => {
      // Shortening the window sweeps immediately, so the history the user is looking at may
      // have just lost entries. Invalidating both is what stops the page showing images that
      // the server has already deleted.
      void queryClient.invalidateQueries({ queryKey: queryKeys.retention(uid) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.history(uid) });
    },
  });
}

export function useDeleteGeneration() {
  const { idToken, user } = useAuth();
  const queryClient = useQueryClient();
  const uid = user?.uid ?? "anonymous";

  return useMutation({
    mutationFn: (generationId: string) =>
      request<{ id: string; deleted: boolean }>(
        `/generate/delete?id=${encodeURIComponent(generationId)}`,
        { method: "DELETE", idToken },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.history(uid) });
    },
  });
}

// ── Admin ────────────────────────────────────────────────────────────────────────────────
//
// The admin queries are **not** keyed by uid, unlike the ones above. They return the same
// data for every admin, so keying them per-user would mean one admin's fetch never warms
// another's cache in the same session — a cost with no correctness gain. The security
// boundary is `require_admin` on the route, not the cache key.

export type AllowedClientDoc = Schemas["AllowedClientDoc"];
export type ClientListResponse = Schemas["ClientListResponse"];
export type AdminHistoryResponse = Schemas["AdminHistoryResponse"];
export type UsageResponse = Schemas["UsageResponse"];
export type ClientUsageRow = Schemas["ClientUsageRow"];
export type MemberUsageRow = Schemas["MemberUsageRow"];
export type OverallUsageRow = Schemas["OverallUsageRow"];
export type RegisterResponse = Schemas["RegisterResponse"];
export type RegisterRowWithPreview = Schemas["RegisterRowWithPreview"];
export type ProfileRequest = Schemas["ProfileRequest"];
export type ProfileUpdateRequest = Schemas["ProfileUpdateRequest"];
export type StylisationLevel = Schemas["StylisationLevel"];

export const adminKeys = {
  clients: () => ["admin", "clients"] as const,
  history: () => ["admin", "history"] as const,
  usage: () => ["admin", "usage"] as const,
  register: () => ["admin", "register"] as const,
  profiles: () => ["admin", "governance-profiles"] as const,
} as const;

export function useAdminClients(): UseQueryResult<ClientListResponse> {
  const { enabled, idToken } = useReady();
  return useQuery({
    queryKey: adminKeys.clients(),
    enabled,
    queryFn: ({ signal }) => request<ClientListResponse>("/admin/clients", { idToken, signal }),
  });
}

export function useAdminHistory(): UseQueryResult<AdminHistoryResponse> {
  const { enabled, idToken } = useReady();
  return useQuery({
    queryKey: adminKeys.history(),
    enabled,
    queryFn: ({ signal }) => request<AdminHistoryResponse>("/admin/history", { idToken, signal }),
  });
}

export function useAdminUsage(): UseQueryResult<UsageResponse> {
  const { enabled, idToken } = useReady();
  return useQuery({
    queryKey: adminKeys.usage(),
    enabled,
    queryFn: ({ signal }) => request<UsageResponse>("/admin/usage", { idToken, signal }),
  });
}

export function useAdminRegister(): UseQueryResult<RegisterResponse> {
  const { enabled, idToken } = useReady();
  return useQuery({
    queryKey: adminKeys.register(),
    enabled,
    queryFn: ({ signal }) => request<RegisterResponse>("/admin/register", { idToken, signal }),
  });
}

export function useAdminProfiles(): UseQueryResult<Schemas["AdminProfileListResponse"]> {
  const { enabled, idToken } = useReady();
  return useQuery({
    queryKey: adminKeys.profiles(),
    enabled,
    queryFn: ({ signal }) =>
      request<Schemas["AdminProfileListResponse"]>("/admin/governance/profiles", {
        idToken,
        signal,
      }),
  });
}

export function useAddClient() {
  const { idToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; clientName: string }) =>
      request<Schemas["AddClientResponse"]>("/admin/clients", {
        method: "POST",
        body: input,
        idToken,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.clients() });
      // Usage lists every client, so a new one has to appear there too.
      void queryClient.invalidateQueries({ queryKey: adminKeys.usage() });
    },
  });
}

export function useRemoveClient() {
  const { idToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      request<{ success: true }>(`/admin/clients?email=${encodeURIComponent(email)}`, {
        method: "DELETE",
        idToken,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.clients() });
      void queryClient.invalidateQueries({ queryKey: adminKeys.usage() });
    },
  });
}

/**
 * Grant, change or clear a client's monthly generation tier.
 *
 * `null` means unlimited and is sent as `null`, not omitted: the route distinguishes "clear
 * the cap" from "leave it alone", and dropping the key would silently do the latter.
 */
export function useSetClientTier() {
  const { idToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; monthlyGenerationLimit: number | null }) =>
      request<Schemas["SetTierResponse"]>("/admin/clients", {
        method: "PATCH",
        body: input,
        idToken,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.usage() });
      void queryClient.invalidateQueries({ queryKey: adminKeys.clients() });
    },
  });
}

export function useSaveProfile() {
  const { idToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    // One mutation for both, because the only difference is whether an id exists — and a
    // second hook would mean a second place that could forget to invalidate.
    mutationFn: (input: ProfileRequest | ProfileUpdateRequest) =>
      request<Schemas["ProfileResponse"]>("/admin/governance/profiles", {
        method: "id" in input ? "PUT" : "POST",
        body: input,
        idToken,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.profiles() });
      // `/generate`'s picker reads the same list through the client-facing route.
      void queryClient.invalidateQueries({ queryKey: queryKeys.governanceProfiles() });
    },
  });
}

export function useDeleteProfile() {
  const { idToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      request<{ success: true }>(
        `/admin/governance/profiles?id=${encodeURIComponent(id)}`,
        { method: "DELETE", idToken },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.profiles() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.governanceProfiles() });
    },
  });
}

/**
 * Delete any client's generation, as an admin.
 *
 * Separate from `useDeleteGeneration` only in what it invalidates: the admin lists are the
 * ones on screen when this is used, and the caller's own history almost certainly is not.
 */
export function useAdminDeleteGeneration() {
  const { idToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (generationId: string) =>
      request<{ id: string; deleted: boolean }>(
        `/generate/delete?id=${encodeURIComponent(generationId)}`,
        { method: "DELETE", idToken },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.history() });
      void queryClient.invalidateQueries({ queryKey: adminKeys.register() });
      void queryClient.invalidateQueries({ queryKey: adminKeys.usage() });
    },
  });
}

// ── Publication tracking ─────────────────────────────────────────────────────────────────
//
// Self-service marking, not an approval workflow — see the doc comment on `GenerationDoc`'s
// publication fields. Both hooks invalidate every list that shows publication state, which is
// what replaces the Next.js card's optimistic local mirror of it.

/** Which lists a publication change can appear in. Invalidated together, for that reason. */
function publicationAffectedKeys(uid: string) {
  return [queryKeys.history(uid), adminKeys.history(), adminKeys.register()];
}

export function usePublishGeneration() {
  const { idToken, user } = useAuth();
  const queryClient = useQueryClient();
  const uid = user?.uid ?? "anonymous";

  return useMutation({
    mutationFn: (input: {
      generationId: string;
      publicationLabel: string;
      publicationUrl: string;
    }) =>
      request<{ success: true }>(
        `/generate/publication?id=${encodeURIComponent(input.generationId)}`,
        {
          method: "POST",
          body: {
            publicationLabel: input.publicationLabel,
            publicationUrl: input.publicationUrl,
          },
          idToken,
        },
      ),
    onSuccess: () => {
      for (const key of publicationAffectedKeys(uid)) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });
}

export function useWithdrawGeneration() {
  const { idToken, user } = useAuth();
  const queryClient = useQueryClient();
  const uid = user?.uid ?? "anonymous";

  return useMutation({
    mutationFn: (generationId: string) =>
      request<{ success: true }>(
        `/generate/publication?id=${encodeURIComponent(generationId)}`,
        { method: "DELETE", idToken },
      ),
    onSuccess: () => {
      for (const key of publicationAffectedKeys(uid)) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });
}

// ── Generate ─────────────────────────────────────────────────────────────────────────────

export type GenerateResponse = Schemas["GenerateResponse"];
export type GenerationResult = Schemas["GenerationResult"];
export type DescribeRequest = Schemas["DescribeRequest"];
export type ImageSeriesWithPreview = Schemas["ImageSeriesWithPreview"];
export type GuardrailConfigResponse = Schemas["GuardrailConfigResponse"];
export type CampaignBriefRequest = Schemas["CampaignBriefRequest"];
export type CampaignBriefResponse = Schemas["CampaignBriefResponse"];

/**
 * The guardrail rules.
 *
 * Cached indefinitely and never refetched on mount: the keyword list changes when an admin
 * edits it on the server, not while somebody is typing a brief, and a refetch per navigation
 * would be a request for a document that is identical every time. It is also not keyed by
 * uid — the rules are the same for everyone, and it is the client's own published policy
 * rather than anybody's data.
 *
 * `useReady` still gates it because the route requires a signed-in caller. A failure leaves
 * the config undefined, which `detectSensitiveTopics` treats as "matches nothing" — see its
 * own doc comment for why that is the right direction, and note that the server still rejects
 * a flagged prompt regardless.
 */
export function useGuardrailConfig(): UseQueryResult<GuardrailConfigResponse> {
  const { enabled, idToken } = useReady();
  return useQuery({
    queryKey: ["guardrail-config"],
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    queryFn: ({ signal }) =>
      request<GuardrailConfigResponse>("/guardrail/config", { idToken, signal }),
  });
}

/** The visual policy profiles a client can apply — the read-only, non-admin view. */
export function useGovernanceProfiles(): UseQueryResult<{ profiles: VisualPolicyProfileDoc[] }> {
  const { enabled, idToken } = useReady();
  return useQuery({
    queryKey: queryKeys.governanceProfiles(),
    enabled,
    queryFn: ({ signal }) =>
      request<{ profiles: VisualPolicyProfileDoc[] }>("/governance/profiles", { idToken, signal }),
  });
}

export function useSeriesList(): UseQueryResult<{ series: ImageSeriesWithPreview[] }> {
  const { enabled, idToken, uid } = useReady();
  return useQuery({
    queryKey: queryKeys.series(uid),
    enabled,
    queryFn: ({ signal }) =>
      request<{ series: ImageSeriesWithPreview[] }>("/series", { idToken, signal }),
  });
}

/**
 * Describe mode: a JSON brief in, a batch of generations out.
 *
 * **Invalidates the rate-limit status on settle, not on success.** A moderation-blocked or
 * malformed request still burns rate-limit slots, so the count can move even when nothing was
 * generated — refreshing only after a success would leave the indicator overstating what is
 * left, which is the direction that misleads.
 *
 * The series list is invalidated too: a series' generation count and reference image change
 * with every image added to it, so a stale list would show the wrong count and no thumbnail
 * for a series' very first shot.
 */
export function useGenerateFromDescription() {
  const { idToken, user } = useAuth();
  const queryClient = useQueryClient();
  const uid = user?.uid ?? "anonymous";

  return useMutation({
    mutationFn: (body: DescribeRequest) =>
      request<GenerateResponse>("/generate/describe", { method: "POST", body, idToken }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.rateLimit(uid) });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.series(uid) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.history(uid) });
    },
  });
}

/**
 * Edit mode: multipart, because a file cannot travel as JSON.
 *
 * The body is built by the caller rather than here — the field list is long, several entries
 * are conditional, and `intendedPublicationContexts` is appended once per value, which is not
 * something a generic object-to-FormData helper would get right without being told.
 */
export function useGenerateFromImage() {
  const { idToken, user } = useAuth();
  const queryClient = useQueryClient();
  const uid = user?.uid ?? "anonymous";

  return useMutation({
    mutationFn: (formData: FormData) =>
      request<GenerateResponse>("/generate/edit", { method: "POST", formData, idToken }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.rateLimit(uid) });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.history(uid) });
    },
  });
}

/** "Get an image recommendation from context" — may legitimately answer "no image". */
export function useCampaignBrief() {
  const { idToken } = useAuth();
  return useMutation({
    mutationFn: (body: CampaignBriefRequest) =>
      request<CampaignBriefResponse>("/prompt/campaign-brief", { method: "POST", body, idToken }),
  });
}

/** "Enrich my prompt" — art-directs the rough brief already on screen. */
export function useCraftPrompt() {
  const { idToken } = useAuth();
  return useMutation({
    mutationFn: (roughPrompt: string) =>
      request<Schemas["PromptCraftResponse"]>("/prompt/craft", {
        method: "POST",
        body: { roughPrompt },
        idToken,
      }),
  });
}

/**
 * Records where in the image a crop should stay centred.
 *
 * Best-effort by design: a failure just means the next export falls back to the last saved
 * point, or to the centre. Nothing on the page depends on it, so it has no error surface.
 */
export function useSaveFocalPoint() {
  const { idToken } = useAuth();
  return useMutation({
    mutationFn: (input: { generationId: string; x: number; y: number }) =>
      request<{ success: true }>(
        `/generate/focal-point?id=${encodeURIComponent(input.generationId)}`,
        { method: "POST", body: { x: input.x, y: input.y }, idToken },
      ),
  });
}
